/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/jsx-no-duplicate-props */
import { CircularProgress, Typography, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import { textFieldStyles } from '../../Configurations/ExpenseEntryConfig';
import { valueAsNumber } from '../../Utils/ValueAsUtils';
import ExpenseTextField from './ExpenseTextField';
import { COLORS } from '../../../utils/Application_Constants';
import { refreshVendors } from '../../Services/ExpenseService';

const styles = makeStyles((theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        ...baseStyles,
        autoCompleteInputRoot: {
            paddingBottom: '0px !important',
            paddingRight: '16px !important',
        },
        autoCompleteInput: {
            ...baseStyles.input,
        },
        autoCompletePaper: {
            fontWeight: 'normal',
        },
        autoCompleteClear: {
            marginRight: '8px',
        },
        refreshIcon: {
            marginRight: theme.spacing(1),
            color: COLORS.LT_JOURNEY,
        },
        refreshButton: {
            width: '100%',
            alignItems: 'left',
            display: 'flex',
        },
        refreshText: {
            width: '100%',
            alignItems: 'left',
            display: 'flex',
            color: COLORS.LT_MIDNIGHT50,
            fontWeight: 400,
        },
        refreshStickyFooter: {
            position: 'sticky',
            bottom: 0,
            backgroundColor: COLORS.LT_MIDNIGHT5,
            borderTop: '1px solid #ddd',
            zIndex: 1,
            textAlign: 'left',
            width: '100%',
            margin: 0,
            '&:hover': {
                backgroundColor: COLORS.LT_MIDNIGHT5,
            },
        },
    };
});

const CustomListbox = React.forwardRef((props, ref) => {
    const { children, refreshLabel, isLoading, cancelLabel, onCancel, onRefresh } = props;
    const classes = styles();

    return (
        <ul {...props} ref={ref}>
            {children}

            {/* Sticky footer inside the list */}
            <li className={classes.refreshStickyFooter}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Button
                        onClick={onRefresh}
                        style={{ width: '100%' }}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: COLORS.LT_JOURNEY,
                            },
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress color="inherit" size={20} />
                        ) : (
                            <RefreshIcon className={classes.refreshIcon} />
                        )}
                        <Typography
                            style={{
                                width: '100%',
                                alignItems: 'left',
                                display: 'flex',
                                color: COLORS.LT_MIDNIGHT50,
                            }}
                            sx={{ fontSize: 'inherit', fontWeight: 500 }}
                            onClick={onRefresh}
                        >
                            {refreshLabel}
                        </Typography>
                    </Button>
                    <Link
                        type="link"
                        style={{ marginRight: '1em', cursor: 'pointer' }}
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </Link>
                </div>
            </li>
        </ul>
    );
});

export default function ExpenseAutocomplete(props) {
    const classes = styles();
    const {
        startAdornment,
        label,
        error,
        helperText,
        required,
        onChange,
        value,
        minLength = 1,
        inputProps,
        getOptionsMethod,
        optionLabelKey,
        optionSelectedKey,
        valueAsMethod,
        placeholder,
        showRefreshVendorButton = false,
        isViewOnly = false,
    } = props;
    const excludedProps = [
        'startAdornment',
        'error',
        'helperText',
        'required',
        'inputProps',
        'minLength',
        'getOptionsMethod',
        'optionLabelKey',
        'optionSelectedKey',
        'valueAsMethod',
        'isViewOnly',
    ];
    const includedProps = Object.keys(props).reduce((acc, key) => {
        const result = acc;
        if (!excludedProps.includes(key)) {
            const { [key]: propValue } = props;
            result[key] = propValue;
        }
        return result;
    }, {});

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const defaultNoOptionsText = `Please enter a minimum of ${minLength} character(s)`;
    const [noOptionsText, setNoOptionsText] = useState(defaultNoOptionsText);
    const [isLoading, setIsLoading] = useState(false);

    const [refreshValue, setRefreshValue] = useState('Refresh');
    const [cancelValue, setCancelValue] = useState('');
    const [isCancelled, setIsCancelled] = useState(false);
    const [isFirstSearch, setIsFirstSearch] = useState(true);

    const handleSearch = async () => {
        setIsLoading(true);
        try {
            const response = await getOptionsMethod(inputValue);
            setOptions(Array.isArray(response) ? response : [response]);
            setIsLoading(false);
            setIsFirstSearch(false);
        } catch (_error) {
            setIsLoading(false);
        } finally {
            setNoOptionsText('No options');
        }
    };

    const resetRefreshButton = () => {
        setRefreshValue('Refresh');
        setCancelValue('');
        setIsLoading(false);
    };

    const handleCancel = () => {
        setIsCancelled(true);
        resetRefreshButton();
    };

    const handleRefresh = async () => {
        setRefreshValue('Refreshing data. May take up to 30 seconds.');
        setCancelValue('Cancel');
        setIsLoading(true);
        setIsCancelled(false);

        try {
            const response = await refreshVendors();

            resetRefreshButton();

            if (isCancelled) {
                return;
            }
            if (response.statusCode !== 200) {
                setRefreshValue('Try again. Failed to refresh data.');
                return;
            }

            setOptions([]);
            handleSearch();
        } catch (_error) {
            resetRefreshButton();
            setRefreshValue('Try again. Failed to refresh data.');
        }
    };

    const debounced = useDebouncedCallback((input) => {
        if (input.length >= minLength && open) {
            handleSearch();
        } else if (!input) {
            setOptions([]);
        }
    }, 1000);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleInputChange = (e, newInputValue, reason) => {
        // TDS-8120 leaving this commented out in case someone changes their mind on this functionatily
        // This solves: After searching for a booking number/clinician id, if I highlight the number to overwrite it, the first number I select is ignored.
        // However, booking details will not be changed until a new booking is selected or the field is cleared. Tested that booking and clinician ids can't be mismatched
        // if (reason && reason === 'input' && value) {
        //	onChange(null);
        //	setOptions([]);
        // }
        if (!newInputValue) {
            setNoOptionsText(defaultNoOptionsText);
        }
        if (reason !== 'reset') {
            debounced(newInputValue);
        }
        if (valueAsMethod) {
            setInputValue(valueAsNumber(newInputValue));
        } else {
            setInputValue(newInputValue);
        }
    };

    const renderEndAdornment = () => {
        if (isLoading) {
            return <CircularProgress color="inherit" size={20} />;
        }

        if (
            showRefreshVendorButton &&
            !isFirstSearch &&
            options.length === 0 &&
            inputValue.length >= minLength
        ) {
            return (
                <Button onClick={handleRefresh}>
                    <RefreshIcon className={classes.refreshIcon} />
                    <Typography className={classes.refreshText}>{refreshValue}</Typography>
                </Button>
            );
        }

        return null;
    };

    return (
        <Autocomplete
            {...includedProps}
            classes={{
                inputRoot: classes.autoCompleteInputRoot,
                input: classes.autoCompleteInput,
                paper: classes.autoCompletePaper,
                clearIndicator: classes.autoCompleteClear,
                disabled: classes.disabled,
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            noOptionsText={noOptionsText}
            options={options}
            onChange={(e, newValue) => onChange(newValue)}
            onInputChange={(e, newInputValue, reason) => {
                handleInputChange(e, newInputValue, reason);
            }}
            disabled={isViewOnly}
            value={value || null}
            inputValue={inputValue}
            loading={isLoading}
            getOptionLabel={(option) =>
                option[optionLabelKey]?.toString() || option.toString() || ''
            }
            isOptionEqualToValue={(option, testValue) =>
                option === testValue || `${option[optionSelectedKey]}` === testValue
            }
            renderInput={(params) => (
                <ExpenseTextField
                    {...params}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && options.length === 1) {
                            onChange(options[0]);
                            setOpen(false);
                            e.preventDefault();
                        }
                    }}
                    label={label}
                    inputProps={{ ...params.inputProps, ...inputProps }}
                    InputProps={{
                        ...params.InputProps,
                        ...inputProps,
                        classes: {
                            root: classes.inputRoot,
                            error: classes.error,
                            disabled: classes.disabled,
                        },
                        startAdornment: startAdornment && startAdornment,
                        endAdornment: renderEndAdornment(),
                    }}
                    placeholder={placeholder}
                    InputLabelProps={{
                        shrink: true,
                        classes: {
                            asterisk: classes.inputLabelAsterisk,
                        },
                    }}
                    required={required}
                    error={error}
                    helperText={helperText}
                />
            )}
            slotProps={
                showRefreshVendorButton
                    ? {
                          listbox: {
                              component: CustomListbox,
                              onRefresh: handleRefresh,
                              refreshLabel: refreshValue,
                              cancelLabel: cancelValue,
                              onCancel: handleCancel,
                              isLoading,
                          },
                      }
                    : undefined
            }
        />
    );
}
